import React, {useCallback, useMemo, useRef, useState} from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import {useContainerModeSliderLayout} from './useContainerModeSliderLayout';
import {VehicleDataDTO} from "../../../../../../../../../../api/generated";
import styles from "./VehicleSlider.module.css";
import {SliderButtonPosition, VehicleSliderButton} from "../vehicleSliderButton/VehicleSliderButton";
import {VehicleCard} from "../vehicleCard/VehicleCard";

interface VehicleSliderProps {
    brandColour: string
    vehicles: VehicleDataDTO[]
}

export const RENDER_DISTANCE = 2

export const VehicleSlider: React.FC<VehicleSliderProps> = ({brandColour, vehicles}) => {

    const [derivativeHeight, setDerivativeHeight] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);

    const slickRef = useRef<Slider>(null);

    const {containerRef, slidesToShow, centerPaddingPx, showNavButtons} = useContainerModeSliderLayout({
        isContainerMode: true,
        isMobile: false,
        vehiclesLength: vehicles.length,
    });

    const [currentVehicleIndex, setCurrentVehicleIndex] = useState(0);

    const handleAfterChange = useCallback((index: number) => {
            setCurrentVehicleIndex(index);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [vehicles],
    )

    const [vehicleImageIndexByVin, setVehicleImageIndexByVin] = useState<Record<string, number>>({});
    const getVehicleImageIndex = (id: string): number => vehicleImageIndexByVin[id] ?? 0;
    const setVehicleImageIndex = (id: string, index: number) => {
        setVehicleImageIndexByVin(prev => ({...prev, [id]: index}));
    };

    const PrevArrow = ({onClick}: any) => (
        <div className={`${styles.sliderArrow} ${styles.sliderArrowLeft}`}>
            <VehicleSliderButton
                position={SliderButtonPosition.BUTTON_LEFT}
                onClick={onClick}
            />
        </div>
    );

    const NextArrow = ({onClick}: any) => (
        <div className={`${styles.sliderArrow} ${styles.sliderArrowRight}`}>
            <VehicleSliderButton
                position={SliderButtonPosition.BUTTON_RIGHT}
                onClick={onClick}
            />
        </div>
    );

    const slickSettings = useMemo(() => ({
        infinite: vehicles.length > 1,
        slidesToShow,
        slidesToScroll: 1,
        speed: 500,
        adaptiveHeight: false,
        swipeToSlide: true,
        afterChange: handleAfterChange,
        arrows: showNavButtons,
        nextArrow: showNavButtons ? <NextArrow/> : undefined,
        prevArrow: showNavButtons ? <PrevArrow/> : undefined,
        initialSlide: 0,
        centerMode: centerPaddingPx > 0,
        centerPadding: `${centerPaddingPx}px`,
        focusOnSelect: true,
        dots: false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [vehicles.length, slidesToShow, centerPaddingPx, showNavButtons]);

    const reportContentHeight = (nextHeaderHeight: number, nextDerivativeHeight: number) => {
        if (nextHeaderHeight > headerHeight) {
            setHeaderHeight(prev => Math.max(prev, nextHeaderHeight));
        }
        if (nextDerivativeHeight > derivativeHeight) {
            setDerivativeHeight(prev => Math.max(prev, nextDerivativeHeight));
        }
    }

    return (
        <div className={styles.wrapper}>
            <div ref={containerRef} className={styles.container}>
                <Slider ref={slickRef} {...slickSettings}>
                    {vehicles.map(item => {
                        const vehicleId = (item.vin || item.new_vehicle_id) as string;
                        return (
                            <VehicleCard
                                brandColour={brandColour}
                                vehicle={item}
                                key={vehicleId}
                                reportContentHeight={reportContentHeight}
                                derivativeHeight={derivativeHeight}
                                headerHeight={headerHeight}
                                isInteractive={currentVehicleIndex === vehicles.findIndex(v => (v.vin || v.new_vehicle_id) === vehicleId) || true}
                                selectedImageIndex={getVehicleImageIndex(vehicleId)}
                                onSelectedImageIndexChange={(idx: any) => setVehicleImageIndex(vehicleId, idx)}
                            />
                        );
                    })}
                </Slider>
            </div>
        </div>
    );
}
